import { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Divider,
  Fab,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Paper,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  Zoom,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CloseIcon from '@mui/icons-material/Close';
import AddCommentIcon from '@mui/icons-material/AddCommentOutlined';
import HistoryIcon from '@mui/icons-material/History';
import TuneIcon from '@mui/icons-material/Tune';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { api } from '@/api/client';
import { OPTIONS_KEY, useAssistantOptions, useConversations, type Effort } from './assistant.api';
import { AssistantChat, type ChatSettings } from './AssistantChat';
import { AssistantConnect } from './AssistantConnect';
import { AssistantHandoff } from './AssistantHandoff';

// Set once this person says the connector is added in their Claude app, so
// the button takes them to Claude from then on.
const VIA_CLAUDE_KEY = 'diwan.assistant.viaClaude';

const EFFORT_LABEL: Record<Effort, string> = {
  low: 'Quick',
  medium: 'Balanced',
  high: 'Thorough',
  xhigh: 'Very thorough',
  max: 'Maximum',
};

/**
 * The AI button every signed-in user sees, and the chat it opens.
 *
 * Shown only where the assistant is switched on for the workspace (from the
 * control plane). What the panel shows depends on where this person stands:
 * their role is not included, they have not connected their Claude account yet, or they
 * are ready — in which case it opens straight onto the chat.
 */
export function AssistantLauncher() {
  const { t } = useTranslation();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const queryClient = useQueryClient();
  const [open, setOpen] = useState(false);
  const options = useAssistantOptions();

  const [conversationId, setConversationId] = useState<string | null>(null);
  // Changes only when the person starts or opens a chat — not when a new chat
  // is given its id halfway through the first answer, which would remount it.
  const [chatKey, setChatKey] = useState(0);
  const [lockedModel, setLockedModel] = useState<string | null>(null);
  const [settings, setSettings] = useState<ChatSettings | null>(null);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const [tuneAnchor, setTuneAnchor] = useState<HTMLElement | null>(null);
  const [viaClaude, setViaClaude] = useState(() => localStorage.getItem(VIA_CLAUDE_KEY) === '1');
  const [settingUp, setSettingUp] = useState(false);
  const conversations = useConversations(open && Boolean(options.data?.available && options.data.connected));

  // The workspace's defaults, until this person chooses otherwise.
  useEffect(() => {
    if (options.data && !settings) {
      setSettings({ model: options.data.defaultModel, effort: options.data.defaultEffort });
    }
  }, [options.data, settings]);

  const disconnect = useMutation({
    mutationFn: () => api.delete('/assistant/credential'),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: OPTIONS_KEY }),
  });

  // Not set up on this server, or not switched on for this workspace in the
  // control plane: no button, rather than one that only ever says "unavailable".
  if (
    options.isError ||
    !options.data ||
    options.data.reason === 'assistant.not_configured' ||
    options.data.reason === 'assistant.disabled'
  ) {
    return null;
  }
  const data = options.data;
  const ready = data.available && data.connected;

  const startNew = () => {
    setConversationId(null);
    setLockedModel(null);
    setChatKey((key) => key + 1);
  };

  const currentModel = lockedModel ?? settings?.model ?? data.defaultModel;
  const model = data.models.find((m) => m.id === currentModel);

  const body = (() => {
    if (data.reason === 'assistant.not_permitted') {
      return (
        <Alert severity="info" sx={{ m: 2 }}>
          {t('Your role does not include the data assistant. Ask your administrator.')}
        </Alert>
      );
    }
    if (!data.connected) {
      if ((viaClaude || data.claudeLinked) && data.connectorUrl && !settingUp) {
        return <AssistantHandoff onSetup={() => setSettingUp(true)} />;
      }
      return (
        <AssistantConnect
          connectorUrl={data.connectorUrl}
          onLinked={() => {
            localStorage.setItem(VIA_CLAUDE_KEY, '1');
            setViaClaude(true);
            setSettingUp(false);
          }}
        />
      );
    }
    if (!settings) return null;
    return (
      <AssistantChat
        key={chatKey}
        conversationId={conversationId}
        settings={{ ...settings, model: currentModel }}
        onConversation={(id, usedModel) => {
          setConversationId(id);
          setLockedModel(usedModel);
        }}
      />
    );
  })();

  return (
    <>
      <Zoom in={!open}>
        <Tooltip title={t('Ask about your data')} placement="left">
          <Fab
            color="primary"
            aria-label={t('Ask about your data')}
            onClick={() => setOpen(true)}
            sx={{ position: 'fixed', bottom: 24, right: 24, zIndex: (th) => th.zIndex.modal - 1 }}
          >
            <AutoAwesomeIcon />
          </Fab>
        </Tooltip>
      </Zoom>

      {open && (
        <Paper
          elevation={8}
          role="dialog"
          aria-label={t('Data assistant')}
          sx={{
            position: 'fixed',
            zIndex: (th) => th.zIndex.modal,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            ...(fullScreen
              ? { inset: 0, borderRadius: 0 }
              : { bottom: 24, right: 24, width: 420, height: 'min(640px, calc(100vh - 48px))', borderRadius: 3 }),
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            spacing={1}
            sx={{ px: 1.5, py: 1, bgcolor: 'primary.main', color: 'primary.contrastText' }}
          >
            <AutoAwesomeIcon fontSize="small" />
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Typography variant="subtitle2" fontWeight={700} noWrap>
                {t('Data assistant')}
              </Typography>
              {ready && model && (
                <Typography variant="caption" sx={{ opacity: 0.85 }} noWrap>
                  {model.label}
                  {settings && model.efforts.length > 0 ? ` · ${t(EFFORT_LABEL[settings.effort])}` : ''}
                </Typography>
              )}
            </Box>
            {ready && (
              <>
                <Tooltip title={t('New chat')}>
                  <IconButton size="small" color="inherit" onClick={startNew}>
                    <AddCommentIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('History')}>
                  <IconButton size="small" color="inherit" onClick={(e) => setHistoryAnchor(e.currentTarget)}>
                    <HistoryIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('Model and effort')}>
                  <IconButton size="small" color="inherit" onClick={(e) => setTuneAnchor(e.currentTarget)}>
                    <TuneIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            <Tooltip title={t('Close')}>
              <IconButton size="small" color="inherit" onClick={() => setOpen(false)}>
                <CloseIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>{body}</Box>
        </Paper>
      )}

      <Menu anchorEl={historyAnchor} open={Boolean(historyAnchor)} onClose={() => setHistoryAnchor(null)} sx={{ zIndex: (th) => th.zIndex.modal + 1 }}>
        {(conversations.data ?? []).length === 0 && (
          <MenuItem disabled>{t('No earlier chats')}</MenuItem>
        )}
        {(conversations.data ?? []).slice(0, 15).map((c) => (
          <MenuItem
            key={c.id}
            selected={c.id === conversationId}
            onClick={() => {
              setConversationId(c.id);
              setLockedModel(c.model);
              setChatKey((key) => key + 1);
              setHistoryAnchor(null);
            }}
            sx={{ maxWidth: 340 }}
          >
            <ListItemText primary={c.title} primaryTypographyProps={{ noWrap: true }} secondary={new Date(c.updatedAt).toLocaleString()} />
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={tuneAnchor} open={Boolean(tuneAnchor)} onClose={() => setTuneAnchor(null)} sx={{ zIndex: (th) => th.zIndex.modal + 1 }}>
        <Box sx={{ px: 2, pt: 1, pb: 1.5, width: 300 }}>
          <Typography variant="overline" color="text.secondary">
            {t('Model')}
          </Typography>
          {lockedModel && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
              {t('A chat keeps its model. Start a new chat to use another.')}
            </Typography>
          )}
          <Stack spacing={0.5}>
            {data.models.map((m) => (
              <Button
                key={m.id}
                size="small"
                variant={m.id === currentModel ? 'contained' : 'outlined'}
                disabled={Boolean(lockedModel) && m.id !== lockedModel}
                onClick={() => settings && setSettings({ ...settings, model: m.id })}
                sx={{ justifyContent: 'space-between' }}
              >
                <span>{m.label}</span>
                <span style={{ opacity: 0.7 }}>{'$'.repeat(m.costTier)}</span>
              </Button>
            ))}
          </Stack>

          {model && model.efforts.length > 0 && settings && (
            <>
              <Typography variant="overline" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
                {t('Effort')}
              </Typography>
              <ToggleButtonGroup
                size="small"
                exclusive
                fullWidth
                value={settings.effort}
                onChange={(_e, value: Effort | null) => value && setSettings({ ...settings, effort: value })}
              >
                {(['low', 'medium', 'high'] as Effort[])
                  .filter((e) => model.efforts.includes(e))
                  .map((e) => (
                    <ToggleButton key={e} value={e}>
                      {t(EFFORT_LABEL[e])}
                    </ToggleButton>
                  ))}
              </ToggleButtonGroup>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {t('More effort means better answers to hard questions, and more of your Claude usage.')}
              </Typography>
            </>
          )}

          <Divider sx={{ my: 1.5 }} />
          <MenuItem
            dense
            onClick={() => {
              setTuneAnchor(null);
              disconnect.mutate();
              startNew();
            }}
          >
            <ListItemIcon>
              <LinkOffIcon fontSize="small" />
            </ListItemIcon>
            <ListItemText
              primary={t('Disconnect Claude account')}
              secondary={data.keyHint ? `…${data.keyHint}` : undefined}
            />
          </MenuItem>
        </Box>
      </Menu>
    </>
  );
}
